import { useCallback, useMemo, useState } from "react";
import { Box, Flex, HStack, IconButton, Stack, Table, Tbody, Td, Th, Thead, Tooltip, Tr, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { createColumnHelper, flexRender, tableFeatures, useTable, type ColumnDef } from "@tanstack/react-table-v9";
import { format, formatDistanceToNow } from "date-fns";
import { FiChevronLeft, FiChevronRight, FiDownload } from "react-icons/fi";
import CDS from "design-system";

import { InternalLink } from "components/Link";
import FilterSortSearchBar from "components/ControlBar/FilterSortSearchBar";
import type { SortOption } from "components/ControlBar/types";
import useSideDrawer from "hooks/useSideDrawer";
import { createPaginationQueryString } from "helpers/queryString";

import { customFormKeys } from "../hooks";
import { fetchCustomFormResponses } from "../api";
import { findAnswer, type CustomForm, type CustomFormResponse } from "../types";
import { formatReadonlyValue } from "../helpers";
import ResponseDetailDrawer from "./ResponseDetailDrawer";
import ExportCsvDialog from "./ExportCsvDialog";

const features = tableFeatures({});
const columnHelper = createColumnHelper<typeof features, CustomFormResponse>();

const SORT_OPTIONS: SortOption[] = [
  { label: "Newest first", value: "submitted_at_desc" },
  { label: "Oldest first", value: "submitted_at_asc" },
  { label: "Submitter (A-Z)", value: "submitter_asc" },
];
const DEFAULT_SORT = "submitted_at_desc";
const PAGE_SIZE = 25;

// Sort values are `<field>_<asc|desc>`; the field portion can contain underscores.
function splitSort(value: string): { sortBy: string; sortDirection: "asc" | "desc" } {
  const idx = value.lastIndexOf("_");
  if (idx === -1) return { sortBy: value, sortDirection: "asc" };
  const direction = value.slice(idx + 1);
  return {
    sortBy: value.slice(0, idx),
    sortDirection: direction === "desc" ? "desc" : "asc",
  };
}

interface Props {
  form: CustomForm;
}

export default function ResponsesGrid({ form }: Props) {
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [pageIndex, setPageIndex] = useState(0);
  const { isOpen: isExportOpen, onOpen: openExport, onClose: closeExport } = useDisclosure();

  const drawer = useSideDrawer<CustomFormResponse, string>({
    getItemId: (r) => (r ? String(r.id) : undefined),
  });

  const queryString = useMemo(() => {
    const { sortBy, sortDirection } = splitSort(sort);
    const params = new URLSearchParams(createPaginationQueryString(pageIndex + 1, PAGE_SIZE));
    if (search) params.set("q", search);
    params.set("sort_by", sortBy);
    params.set("sort_direction", sortDirection);
    return `?${params.toString()}`;
  }, [search, sort, pageIndex]);

  const { data, isLoading } = useQuery({
    queryKey: [...customFormKeys.responses(form.id), queryString],
    queryFn: async ({ signal }) => fetchCustomFormResponses(form.id, queryString, signal),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  const responses = data?.data ?? [];
  const totalPages = data?.meta?.pagination?.totalPages ?? 1;

  const fields = useMemo(
    () => [...(form.customFormFields ?? [])].sort((a, b) => a.position - b.position),
    [form.customFormFields],
  );

  // Archived fields that at least one response still answered — mirrors the CSV export
  // (ExportCsvDialog) so removing a question doesn't drop its answers from the grid.
  const archivedFields = useMemo(() => form.archivedReferencedFields ?? [], [form.archivedReferencedFields]);

  const columns = useMemo(() => {
    const submitter = columnHelper.accessor((row) => row, {
      id: "submitter",
      header: "Submitted by",
      cell: (info) => {
        const r = info.getValue();
        const name = r.submittedBy?.fullName ?? "—";
        const email = r.submittedBy?.workEmail;
        return (
          <Stack spacing={0}>
            <CDS.Text12 fontWeight="bold">{name}</CDS.Text12>
            {email && <CDS.Text12 color="fg.subtle">{email}</CDS.Text12>}
          </Stack>
        );
      },
    });

    const submittedAt = columnHelper.accessor((row) => row.submittedAt, {
      id: "submittedAt",
      header: "Submitted",
      cell: (info) => {
        const value = info.getValue();
        if (!value) return <CDS.Text12>—</CDS.Text12>;
        const parsed = new Date(value);
        return (
          <Tooltip label={format(parsed, "MMM d, yyyy 'at' h:mm a")} placement="top" hasArrow>
            <Box as="span" cursor="default">
              <CDS.Text12 whiteSpace="nowrap">{formatDistanceToNow(parsed, { addSuffix: true })}</CDS.Text12>
            </Box>
          </Tooltip>
        );
      },
    });

    const answerColumns = fields.map((field) =>
      columnHelper.accessor((row) => findAnswer(row.answers, field.uuid), {
        id: `answer-${field.uuid}`,
        header: field.label,
        cell: (info) => (
          <CDS.Text12 whiteSpace="nowrap">{formatReadonlyValue(field, info.getValue())}</CDS.Text12>
        ),
      }),
    );

    const archivedAnswerColumns = archivedFields.map((field) =>
      columnHelper.accessor((row) => findAnswer(row.answers, field.uuid), {
        id: `answer-archived-${field.uuid}`,
        header: `${field.label} (archived)`,
        cell: (info) => (
          <CDS.Text12 whiteSpace="nowrap" color="fg.subtle">
            {formatReadonlyValue(field, info.getValue())}
          </CDS.Text12>
        ),
      }),
    );

    const view = columnHelper.display({
      id: "view",
      header: "",
      cell: (info) => (
        <Box textAlign="right">
          <InternalLink
            to="#"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              drawer.expandClickHandlerByItem(info.row.original);
            }}
            color="blue.600"
          >
            <CDS.Text12 fontWeight="bold" whiteSpace="nowrap">
              View
            </CDS.Text12>
          </InternalLink>
        </Box>
      ),
    });

    return [submitter, submittedAt, ...answerColumns, ...archivedAnswerColumns, view] as ColumnDef<
      typeof features,
      CustomFormResponse,
      unknown
    >[];
  }, [fields, archivedFields, drawer]);

  const table = useTable({
    features,
    columns,
    data: responses,
  });

  const hasActiveFilters = !!search;

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPageIndex(0);
  }, []);

  const onSortChange = useCallback((value: string) => {
    setSort(value);
    setPageIndex(0);
  }, []);

  return (
    <Stack spacing={4} w="100%">
      <FilterSortSearchBar
        searchValue={search}
        onSearchChange={onSearchChange}
        searchPlaceholder="Search by name or email..."
        currentSort={sort}
        defaultSort={DEFAULT_SORT}
        onSortChange={onSortChange}
        sortOptions={SORT_OPTIONS}
        activeFilters={[]}
        onReset={() => {
          setSearch("");
          setSort(DEFAULT_SORT);
          setPageIndex(0);
        }}
        hasActiveFilters={hasActiveFilters}
      />

      <Flex justify="flex-end" align="center">
        <HStack>
          <CDS.Button
            intent={CDS.Button.intent.secondary}
            variant={CDS.Button.variant.outline}
            size="sm"
            leftIcon={{ customIcon: <FiDownload /> }}
            onClick={openExport}
          >
            Export CSV
          </CDS.Button>
        </HStack>
      </Flex>

      <Box borderWidth="1px" borderColor="border.subtle" borderRadius="md" overflowX="auto" w="100%">
        <Table size="sm" variant="simple" sx={{ minWidth: "max-content" }}>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header, index) => (
                  <Th
                    key={header.id}
                    whiteSpace="nowrap"
                    position={index === 0 ? "sticky" : undefined}
                    left={index === 0 ? 0 : undefined}
                    bg="chakra-body-bg"
                    zIndex={index === 0 ? 1 : undefined}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {!isLoading && responses.length === 0 && (
              <Tr>
                <Td colSpan={columns.length} textAlign="center" borderBottom="none">
                  <CDS.Text14 color="fg.subtle">No responses yet.</CDS.Text14>
                </Td>
              </Tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getAllCells().map((cell, index) => (
                  <Td
                    key={cell.id}
                    position={index === 0 ? "sticky" : undefined}
                    left={index === 0 ? 0 : undefined}
                    bg="chakra-body-bg"
                    zIndex={index === 0 ? 1 : undefined}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <HStack justify="flex-end" spacing={3}>
          <CDS.Text12 color="fg.subtle">
            Page {pageIndex + 1} of {totalPages}
          </CDS.Text12>
          <IconButton
            aria-label="Previous page"
            icon={<FiChevronLeft />}
            size="sm"
            variant="ghost"
            isDisabled={pageIndex === 0}
            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
          />
          <IconButton
            aria-label="Next page"
            icon={<FiChevronRight />}
            size="sm"
            variant="ghost"
            isDisabled={pageIndex + 1 >= totalPages}
            onClick={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
          />
        </HStack>
      )}

      <ResponseDetailDrawer
        responseId={drawer.selectedId}
        isOpen={drawer.isOpen}
        onClose={drawer.onClose}
      />
      <ExportCsvDialog formId={form.id} isOpen={isExportOpen} onClose={closeExport} />
    </Stack>
  );
}
